(() => {
  if (customElements.get('hx-recent-activity')) return;
  class RecentActivity extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.maxAge = Number(this.dataset.maxAge || 7) * 86400000;
      this.seenKey = 'hx-recent-activity-v2';
      this.seen = this.readSeen();
      this.sessionCount = Number(this.readStorage(this.seenKey + ':count') || 0);
      this.dismissed = this.readStorage(this.seenKey + ':dismissed') === '1';
      this.lastRefresh = Date.now();
      this.querySelector('[data-activity-close]').addEventListener('click', () => {
        this.dismissed = true;
        this.writeStorage(this.seenKey + ':dismissed', '1');
        this.hide();
        clearTimeout(this.nextTimer);
      }, options);
      this.addEventListener('mouseenter', () => this.pause(), options);
      this.addEventListener('mouseleave', () => this.resume(), options);
      this.addEventListener('focusin', () => this.pause(), options);
      this.addEventListener('focusout', () => this.resume(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.hide();
      }, options);
      window.addEventListener('resize', () => this.position(), options);
      this.overlayObserver = new MutationObserver(() => {
        if (this.blocked()) this.hide();
        this.position();
      });
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.querySelectorAll('dialog').forEach(dialog => this.overlayObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
      document.addEventListener('hx:purchase-intent', () => { this.hide(); this.schedule(32000); }, options);
      this.schedule(Number(this.dataset.delay || 20) * 1000);
    }
    readStorage(key) { try { return sessionStorage.getItem(key); } catch { return null; } }
    writeStorage(key, value) { try { sessionStorage.setItem(key, value); } catch { /* Storage may be restricted. */ } }
    readSeen() {
      try {
        const value = JSON.parse(this.readStorage(this.seenKey) || '[]');
        return new Set(Array.isArray(value) ? value.filter(item => typeof item === 'string').slice(-40) : []);
      } catch { return new Set(); }
    }
    blocked() {
      return document.hidden || document.documentElement.dataset.hxPurchaseIntent === 'true' || !!document.querySelector('dialog[open], details[open].menu-opening, cart-drawer.active, arte-cart-drawer[open], arte-cart-drawer.is-open, .v3-cart-drawer.is-open, .hx-product-lightbox[open]') || document.body.classList.contains('hx-product-lightbox-open');
    }
    position() {
      let bottom = 24;
      const sticky = document.querySelector('.hx-product-sticky');
      if (sticky && !sticky.hidden) {
        const rect = sticky.getBoundingClientRect();
        if (rect.height > 0 && rect.top < innerHeight && rect.bottom > 0) bottom = Math.max(bottom, innerHeight - rect.top + 12);
      }
      if (innerWidth < 750) {
        const launcher = document.querySelector('[data-capture-open]');
        if (launcher && !launcher.hidden && launcher.getBoundingClientRect().height > 0) bottom = Math.max(bottom, innerHeight - launcher.getBoundingClientRect().top + 12);
      }
      this.style.setProperty('--hx-activity-bottom', bottom + 'px');
    }
    schedule(delay = 32000) {
      clearTimeout(this.nextTimer);
      if (this.dismissed || this.sessionCount >= 3) return;
      this.nextTimer = setTimeout(() => this.showNext(), delay);
    }
    async refresh() {
      if (Date.now() - this.lastRefresh < 120000 || this.blocked()) return;
      this.lastRefresh = Date.now();
      try {
        const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
        url.searchParams.set('sections', this.dataset.section);
        const response = await fetch(url, { signal: this.events.signal, credentials: 'same-origin' });
        if (!response.ok) return;
        const sections = await response.json();
        const markup = sections[this.dataset.section];
        if (typeof markup !== 'string') return;
        const page = new DOMParser().parseFromString(markup, 'text/html');
        const updated = page.querySelector('[data-activity-events]');
        if (updated) this.querySelector('[data-activity-events]').replaceChildren(...updated.childNodes);
      } catch { /* Keep the last known real events when the network is unavailable. */ }
    }
    async showNext() {
      if (!this.isConnected || this.dismissed || this.sessionCount >= 3) return;
      if (this.blocked()) { this.schedule(5000); return; }
      await this.refresh();
      if (this.blocked() || this.dismissed || !this.isConnected) { this.schedule(5000); return; }
      const now = Date.now();
      const templates = [...this.querySelectorAll('template[data-activity-event]')];
      const next = templates.find(template => {
        const date = Date.parse(template.dataset.date);
        const key = template.dataset.handle + '|' + template.dataset.date;
        return Number.isFinite(date) && now >= date && now - date <= this.maxAge && !this.seen.has(key);
      });
      if (!next) { this.schedule(120000); return; }
      const key = next.dataset.handle + '|' + next.dataset.date;
      this.querySelector('[data-activity-image]').replaceChildren(next.content.cloneNode(true));
      const link = this.querySelector('[data-activity-link]');
      const url = new URL(next.dataset.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
      link.href = url.href;
      this.querySelector('[data-activity-title]').textContent = next.dataset.title;
      const locationRow = this.querySelector('[data-activity-location]');
      const city = next.dataset.city?.trim() || '';
      let country = next.dataset.country?.trim() || '';
      const code = next.dataset.countryCode?.trim().toUpperCase() || '';
      if (/^[A-Z]{2}$/.test(code) && typeof Intl.DisplayNames === 'function') {
        try { country = new Intl.DisplayNames([document.documentElement.lang || 'fr'], { type: 'region' }).of(code) || country; }
        catch {}
      }
      if (locationRow) {
        locationRow.querySelector('[data-activity-location-label]').textContent = [city, country].filter(Boolean).join(', ');
        locationRow.hidden = !city && !country;
      }
      this.position();
      this.classList.remove('is-leaving');
      this.hidden = false;
      this.seen.add(key);
      this.writeStorage(this.seenKey, JSON.stringify([...this.seen].slice(-40)));
      this.sessionCount += 1;
      this.writeStorage(this.seenKey + ':count', String(this.sessionCount));
      this.remaining = 8000;
      this.resume();
    }
    pause() {
      if (this.hidden || !this.hideTimer) return;
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
      this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
    }
    resume() {
      if (this.hidden || this.hideTimer || this.matches(':hover,:focus-within')) return;
      this.startedAt = Date.now();
      this.hideTimer = setTimeout(() => this.hide(), this.remaining);
    }
    hide() {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
      if (this.hidden) return;
      this.classList.add('is-leaving');
      clearTimeout(this.exitTimer);
      this.exitTimer = setTimeout(() => { this.hidden = true; this.classList.remove('is-leaving'); }, 330);
      this.schedule();
    }
    disconnectedCallback() {
      clearTimeout(this.nextTimer);
      clearTimeout(this.hideTimer);
      clearTimeout(this.exitTimer);
      this.overlayObserver?.disconnect();
      this.events?.abort();
      this.ready = false;
    }
  }
  customElements.define('hx-recent-activity', RecentActivity);
})();
